import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Globe,
  Users,
  ShieldCheck,
  Anchor,
  Target,
  Ship,
  CheckCircle2,
  MapPin,
  ArrowRight,
} from "lucide-react";
import Seo from "../components/Seo";
import backgroundImageHeader from "../img/ah.webp";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Memastikan scroll berada di paling atas saat berpindah halaman
    window.scrollTo(0, 0);
    setIsVisible(true);

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "MarineService",
      name: "PT Kamandanu Jaya Samudera",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Jl. Warakas 3 No.23, RT.010 RW.004, 2nd Floor",
        addressLocality: "North Jakarta",
        addressRegion: "DKI Jakarta",
        postalCode: "14340",
        addressCountry: "ID",
      },
      description:
        "Leading Indonesian crewing agency providing skilled, STCW-certified seafarers and comprehensive vessel management.",
      url: "https://www.ptkamandanujayasamudera.com/about",
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Seo
        title="About Us | PT Kamandanu Jaya Samudera - Expert Manning Agency"
        description="PT Kamandanu Jaya Samudera: North Jakarta's premier crewing agency. Managing 10,800+ seafarers with a global network and 24/7 digital monitoring."
        url="/about"
      />

      <main
        className="bg-[#F8FAFC] min-h-screen text-[#0A2540] antialiased"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {/* ─── HERO HEADER SECTION (pt-28 memberi ruang aman agar TIDAK KETUTUP NAVBAR) ─── */}
      <section className="relative pt-28 sm:pt-32 pb-20 bg-[#0A2540] overflow-hidden">
          {/* Background Image dengan Overlay */}
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${backgroundImageHeader})`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/80 via-[#0A2540]/90 to-[#0A2540]" />

          {/* Hero Content */}
          <div className="relative z-10 text-center text-white max-w-7xl mx-auto px-4">
            <div
              className={`transition-all duration-1000 ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-10 opacity-0"
              }`}
            >

              <h1 className="text-4xl md:text-6xl font-black text-white mb-6 drop-shadow-lg">
                Navigating{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-200">
                  Human Capital
                </span>
              </h1>

              <p className="text-xl text-blue-50/90 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
                Empowering the global maritime industry with Indonesia's most
                skilled, STCW-certified, and dedicated seafarers.
              </p>
            </div>
          </div>
        </section>

        {/* ─── MAIN CONTENT CONTAINER ─── */}
        <div className="max-w-7xl mx-auto px-4 pb-32 pt-20">
          {/* WHO WE ARE & GLOBAL NETWORK */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16 sm:mb-24">
            {/* Main Strategic Manning Card */}
            <div
              className={`lg:col-span-2 bg-white rounded-[2.5rem] p-8 sm:p-12 shadow-xl border border-slate-100/80 transition-all duration-700 hover:shadow-2xl ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-10 opacity-0"
              }`}
            >
              <div className="inline-flex items-center justify-center p-3.5 bg-blue-50 rounded-2xl text-blue-600 mb-6">
                <Ship size={32} />
              </div>

              <h2 className="text-3xl sm:text-4xl font-black text-[#0A2540] mb-6 tracking-tight">
                Strategic Manning Partner
              </h2>

              <div className="space-y-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                <p>
                  <strong className="text-slate-900 font-bold">
                    PT Kamandanu Jaya Samudera
                  </strong>{" "}
                  specializes in attracting, developing, and retaining elite crew
                  members. Based in North Jakarta, we serve as a vital link in
                  the global maritime supply chain, meeting the rising demand
                  for highly capable energy and commercial fleet crew.
                </p>

                <p>
                  Supported by maritime industry veterans and a{" "}
                  <strong className="text-slate-900 font-bold">
                    centralized digital management system
                  </strong>
                  , we guarantee full regulatory compliance, filing every
                  certificate, endorsement, and visa electronically for complete
                  operational transparency.
                </p>

                {/* Badges / Metrics */}
                <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2.5 bg-slate-50 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-700 border border-slate-200/60 shadow-sm">
                    <MapPin size={18} className="text-blue-600" />
                    <span>North Jakarta, Indonesia</span>
                  </div>
                  <div className="flex items-center gap-2.5 bg-blue-50 px-4 py-2.5 rounded-xl text-sm font-bold text-blue-900 border border-blue-100 shadow-sm">
                    <Users size={18} className="text-blue-600" />
                    <span>10,800+ Seafarers Managed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Global Network Card */}
            <div
              className={`bg-gradient-to-br from-[#0A2540] via-blue-900 to-blue-800 rounded-[2.5rem] p-8 sm:p-10 text-white shadow-2xl transition-all duration-700 delay-200 hover:-translate-y-1 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "translate-x-10 opacity-0"
              }`}
            >
              <div className="h-full flex flex-col justify-between space-y-8">
                <div>
                  <div className="p-4 rounded-2xl bg-white/10 w-fit backdrop-blur-md border border-white/10 mb-6">
                    <Globe size={36} className="text-cyan-300" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black mb-4 tracking-tight">
                    Global Network
                  </h3>
                  <p className="text-blue-100/90 leading-relaxed text-base">
                    We provide shipowners and vessel managers instant access to
                    a highly competent crew pool, ensuring your fleet operates
                    without costly downtime.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/15 text-center">
                  <p className="text-xs font-extrabold text-cyan-300 uppercase tracking-widest mb-2">
                    Core Commitment
                  </p>
                  <p className="text-xl sm:text-2xl font-black italic tracking-wide text-white">
                    "Quality & Integrity"
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* VISION & MISSION SECTION */}
          <section className="grid md:grid-cols-2 gap-8 mb-20">
            {/* Vision Card */}
            <div className="bg-white p-8 sm:p-12 rounded-[2.5rem] shadow-xl border border-slate-100/80 hover:shadow-2xl transition-all">
              <div className="inline-flex p-4 rounded-2xl bg-cyan-50 text-cyan-600 mb-8">
                <Target size={36} />
              </div>
              <h3 className="text-3xl font-black text-[#0A2540] mb-6 tracking-tight">
                Our Vision
              </h3>
              <blockquote className="text-slate-600 text-lg sm:text-xl leading-relaxed italic border-l-4 border-blue-600 pl-6 my-2 font-medium">
                "To be the global benchmark in maritime manning, recognized for
                unwavering reliability, professional integrity, and the
                exceptional quality of our seafarers."
              </blockquote>
            </div>

            {/* Mission Card */}
            <div className="bg-[#0A2540] p-8 sm:p-12 rounded-[2.5rem] shadow-2xl text-white relative overflow-hidden flex flex-col justify-between">
              <Award
                size={160}
                className="absolute -top-6 -right-6 p-4 opacity-5 text-white pointer-events-none"
              />
              <div>
                <h3 className="text-3xl font-black mb-8 flex items-center gap-3 tracking-tight">
                  <ShieldCheck size={36} className="text-blue-400 shrink-0" />
                  Our Mission
                </h3>
                <ul className="space-y-5">
                  {[
                    "Execute rigorous, transparent recruitment for maximum crew competence.",
                    "Cultivate a performance-driven environment for our global crew.",
                    "Protect and maintain client vessels and maritime assets with utmost duty.",
                    "Adhere strictly to international maritime regulations (MLC 2006 & STCW).",
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <CheckCircle2 className="text-cyan-400 shrink-0 mt-1 w-5 h-5" />
                      <p className="text-blue-100 text-base sm:text-lg font-medium leading-normal">
                        {text}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ─── CALL TO ACTION SECTION ─── */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[2.5rem] p-8 sm:p-12 text-white text-center shadow-xl relative overflow-hidden">
            <div className="relative z-10 max-w-3xl mx-auto space-y-6">
              <h3 className="text-3xl sm:text-4xl font-black tracking-tight">
                Ready to Partner with Us?
              </h3>
              <p className="text-blue-100 text-base sm:text-lg leading-relaxed">
                Connect with PT Kamandanu Jaya Samudera today to secure top-tier
                Indonesian seafarers and professional crewing solutions for your fleet.
              </p>
              <div className="pt-2">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white hover:bg-slate-100 text-[#0A2540] font-bold rounded-2xl transition-all shadow-lg hover:shadow-white/20 hover:-translate-y-0.5"
                >
                  <span>CONTACT OUR TEAM</span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style jsx global>{`
        @import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800;900&display=swap");
      `}</style>
    </>
  );
};

export default About;